import React, { useState, useCallback, useEffect } from "react";

import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import LabelIcon from "@mui/icons-material/Label";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
  alpha,
} from "@mui/material";
import { type Theme } from "@mui/material/styles";

import { OrganizationEndpoints } from "@generated/organization";
import { orgSessionManager } from "src/domains/auth/services/orgSessionManager";
import { apiBaseUrls } from "src/platform/constants/shared-constants";
import { EmptyState } from "src/shell/components/EmptyState";
import { ErrorMessage } from "src/shell/components/ErrorMessage";
import {
  getCardStyles,
  getFocusRingSx,
  sectionLabelSx,
} from "src/shell/styles/themeUtils";

import type { TagSchemaDefinition } from "../../types/OrganizationTypes";

interface TagSchemaTabProps {
  orgAlias: string;
}

const TagSchemaTab: React.FC<TagSchemaTabProps> = ({ orgAlias }) => {
  const theme = useTheme();
  const [schema, setSchema] = useState<TagSchemaDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newKey, setNewKey] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [newRequired, setNewRequired] = useState(false);
  const [newAllowedValues, setNewAllowedValues] = useState("");
  const [newDefaultValue, setNewDefaultValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);

  const fetchSchema = useCallback(async () => {
    if (!orgAlias) return;
    setLoading(true);
    try {
      const token = orgSessionManager.getToken() ?? "";
      const res = await fetch(
        `${apiBaseUrls.BACKEND}${OrganizationEndpoints.GET_TAG_SCHEMA.replace(":alias", orgAlias)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error("Failed to load tag schema");
      const data = (await res.json()) as TagSchemaDefinition[];
      setSchema(
        data.map((d) => ({
          ...d,
          type:
            d.allowedValues.length > 0
              ? ("enum" as const)
              : ("string" as const),
        })),
      );
    } catch {
      setError("Failed to load tag schema");
    } finally {
      setLoading(false);
    }
  }, [orgAlias]);

  useEffect(() => {
    void fetchSchema();
  }, [fetchSchema]);

  const handleCreate = async () => {
    setSaving(true);
    setDialogError(null);
    try {
      const token = orgSessionManager.getToken() ?? "";
      const allowedValues = newAllowedValues
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean);
      const res = await fetch(
        `${apiBaseUrls.BACKEND}${OrganizationEndpoints.CREATE_TAG_SCHEMA.replace(":alias", orgAlias)}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            key: newKey,
            label: newLabel,
            description: newDescription || undefined,
            required: newRequired,
            allowedValues,
            defaultValue: newDefaultValue || undefined,
          }),
        },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message ?? "Failed to create tag schema");
      }
      await fetchSchema();
      setDialogOpen(false);
      setNewKey("");
      setNewLabel("");
      setNewDescription("");
      setNewRequired(false);
      setNewAllowedValues("");
      setNewDefaultValue("");
    } catch (err) {
      setDialogError(err instanceof Error ? err.message : "Failed to create");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    const token = orgSessionManager.getToken() ?? "";
    await fetch(
      `${apiBaseUrls.BACKEND}${OrganizationEndpoints.DELETE_TAG_SCHEMA.replace(":alias", orgAlias).replace(":id", String(id))}`,
      { method: "DELETE", headers: { Authorization: `Bearer ${token}` } },
    );
    await fetchSchema();
  };

  if (loading) {
    return <CircularProgress size={24} sx={{ mt: 2 }} />;
  }

  if (error) {
    return <ErrorMessage message={error} sx={{ my: 2 }} />;
  }

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
          {schema.length} tag definition{schema.length !== 1 ? "s" : ""}
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setDialogOpen(true)}
          sx={{ textTransform: "none", fontWeight: 500 }}
        >
          Add Tag
        </Button>
      </Stack>

      {schema.length === 0 ? (
        <Card sx={getCardStyles(theme)}>
          <EmptyState
            icon={LabelIcon}
            title="No tag schema defined"
            subtitle="Define required tags to enable FinOps cost allocation and chargeback reporting."
            actionLabel="Add tag"
            onAction={() => setDialogOpen(true)}
          />
        </Card>
      ) : (
        <Card sx={getCardStyles(theme)}>
          <TableContainer>
            <Table sx={{ minWidth: 520 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellSx}>Key</TableCell>
                  <TableCell sx={headerCellSx}>Type</TableCell>
                  <TableCell sx={headerCellSx}>Required</TableCell>
                  <TableCell sx={headerCellSx}>Description</TableCell>
                  <TableCell align="right" sx={headerCellSx}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {schema.map((def, index) => (
                  <TableRow
                    key={def.id}
                    sx={{
                      transition: "background 150ms ease",
                      "&:hover": {
                        bgcolor: alpha(theme.palette.primary.main, 0.04),
                      },
                      "& td": {
                        borderBottom:
                          index < schema.length - 1
                            ? `1px solid ${alpha(theme.palette.divider, 0.06)}`
                            : "none",
                        py: 1.5,
                      },
                    }}
                  >
                    <TableCell>
                      <Stack spacing={0.25}>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          fontFamily="monospace"
                        >
                          {def.key}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {def.label}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={def.type === "enum" ? "Enum" : "String"}
                        size="small"
                        color={def.type === "enum" ? "primary" : "default"}
                        variant="outlined"
                        sx={{ height: 22, fontSize: "0.7rem" }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={def.required ? "Required" : "Optional"}
                        size="small"
                        color={def.required ? "success" : "default"}
                        variant="outlined"
                        sx={{ height: 22, fontSize: "0.7rem" }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {def.description ??
                          (def.allowedValues.length > 0
                            ? def.allowedValues.join(", ")
                            : "No description")}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => void handleDelete(def.id)}
                        sx={{
                          color: "text.disabled",
                          "&:hover": { color: "error.main" },
                        }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      <Dialog
        open={dialogOpen}
        onClose={() => !saving && setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Tag Definition</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            {dialogError && (
              <Alert severity="error" sx={{ borderRadius: 1 }}>
                {dialogError}
              </Alert>
            )}
            <TextField
              label="Tag Key"
              fullWidth
              value={newKey}
              onChange={(e) => setNewKey(e.target.value)}
              placeholder="e.g. cost_center"
              helperText="Lowercase, used in billing data. Cannot be changed later."
              disabled={saving}
              sx={getFocusRingSx(theme)}
            />
            <TextField
              label="Display Label"
              fullWidth
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              placeholder="e.g. Cost Center"
              disabled={saving}
              sx={getFocusRingSx(theme)}
            />
            <TextField
              label="Description"
              fullWidth
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              placeholder="e.g. Department cost center code"
              disabled={saving}
              sx={getFocusRingSx(theme)}
            />
            <Box>
              <Typography variant="subtitle2" sx={{ ...sectionLabelSx, mb: 1 }}>
                Required
              </Typography>
              <Select
                fullWidth
                value={newRequired ? "yes" : "no"}
                onChange={(e) => setNewRequired(e.target.value === "yes")}
                disabled={saving}
                sx={getFocusRingSx(theme)}
              >
                <MenuItem value="no">
                  Optional — projects can skip this tag
                </MenuItem>
                <MenuItem value="yes">
                  Required — projects must provide this tag
                </MenuItem>
              </Select>
            </Box>
            <TextField
              label="Allowed Values (comma-separated)"
              fullWidth
              value={newAllowedValues}
              onChange={(e) => setNewAllowedValues(e.target.value)}
              placeholder="e.g. engineering, marketing, operations"
              helperText="Leave empty for free-text tags"
              disabled={saving}
              sx={getFocusRingSx(theme)}
            />
            <TextField
              label="Default Value (optional)"
              fullWidth
              value={newDefaultValue}
              onChange={(e) => setNewDefaultValue(e.target.value)}
              placeholder="e.g. unassigned"
              helperText="Used when a project doesn't provide this tag"
              disabled={saving}
              sx={getFocusRingSx(theme)}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setDialogOpen(false)}
            disabled={saving}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleCreate()}
            disabled={saving || !newKey.trim() || !newLabel.trim()}
            sx={{
              textTransform: "none",
              "&:active": { transform: "scale(0.98)" },
            }}
          >
            {saving ? <CircularProgress size={18} color="inherit" /> : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

const headerCellSx = {
  fontSize: "0.6875rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  color: "text.secondary",
  borderBottom: (theme: Theme) =>
    `1px solid ${alpha(theme.palette.divider, 0.06)}`,
};

export default TagSchemaTab;
